/**
 * © 2016 Becki Authors. See the AUTHORS file found in the top-level directory
 * of this distribution.
 */

import { TyrionAPI, IPerson, ILoginResult, INotification } from './TyrionAPI';
import * as Rx from 'rxjs';
import {
    BadRequest, BugFoundError, CodeCompileError,
    CodeError, IError, InternalServerError, InvalidBody, LostConnectionError, ObjectNotFound, PermissionMissingError,
    RestRequest, RestResponse,
    UnauthorizedError, UnsupportedException, UserNotValidatedError
} from '../services/_backend_class/Responses';
import { WebsocketService } from '../services/websocket/WebsocketService';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';

declare const BECKI_VERSION: string;
// INTERFACES

export declare type NetworkStatus = 'NOT_YET_FIRST_CONNECTED'|'FREEZED'|'SHUT_DOWN'|'SYNCHRONIZATION_IN_PROGRESS'|'OFFLINE'|'ONLINE'|'UNKNOWN_LOST_CONNECTION_WITH_SERVER';

// REQUEST CLASSES
export interface OnlineChangeStatus {
    model: ('Hardware' | 'Instance' | 'HomerServer' | 'CompilationServer');
    model_id: 'uuid';
    online_state: NetworkStatus;
}

export interface ModelChangeStatus {
    model: ('ProjectsRefreshAfterInvite' | 'Instance' | 'Project' | 'HomerServer' | 'Hardware' | 'Widget' | 'CProgram' | 'MProgram' | 'BProgram' | 'ActualizationProcedure' | 'CProgramUpdatePlan' | 'WidgetVersion' | 'CProgramVersion' | 'Library' | 'LibraryVersion');
    model_id: 'uuid';
}

export interface Networkable {
    online_state: NetworkStatus;
}

export function handleStatus(networkable: Networkable): (status: NetworkStatus) => void {
    return (status: NetworkStatus) => {
        if (!networkable.online_state || status !== 'SYNCHRONIZATION_IN_PROGRESS') {
            networkable.online_state = status;
        }
    }
}

// BECKI BACKEND

export abstract class TyrionApiBackend extends TyrionAPI {

    public static host = 'tyrion.nexuri.cz';
    public static protocol = 'https';

    public wsProtocol: string = 'ws';

    public requestProxyServerUrl = 'http://127.0.0.1:4000/fetch/';

    private beckiBeta: boolean = true; // Show Beta - next to logo

    public notificationReceived: Rx.Subject<INotification> = new Rx.Subject<INotification>();
    public webSocketErrorOccurred: Rx.Subject<any> = new Rx.Subject<any>();

    // Subscribers on Websocket for change online state or update object
    public onlineStatus: Rx.Subject<OnlineChangeStatus> = new Rx.Subject<OnlineChangeStatus>();
    public objectUpdateTyrionEcho: Rx.Subject<ModelChangeStatus> = new Rx.Subject<ModelChangeStatus>();

    protected networkStatusObservables: { [id: string]: BehaviorSubject<NetworkStatus> } = {};

    // Task counter for list of requests
    public tasks: number = 0;

    protected personInfoSnapshotDirty: boolean = true;
    public personInfoSnapshot: IPerson = null;
    public personInfo: Rx.Subject<IPerson> = new Rx.Subject<IPerson>();

    protected websocketService: WebsocketService = null;

    protected websocketErrorShown: boolean = false;

    protected abstract increaseTasks();
    protected abstract decreaseTasks();

    // CONSTRUCTOR
    public constructor() {
        super();

        if (location && location.hostname) {
            if (location.hostname.indexOf('portal.') === 0) {
                TyrionApiBackend.host = location.hostname.replace('portal.', 'tyrion.');
            } else {
                // TyrionApiBackend.host = location.hostname + ':9000';
                TyrionApiBackend.host = 'tyrion.nexuri.cz';
            }
        }

        if (location && location.protocol) {
            if (location.protocol === 'https:') {
                TyrionApiBackend.protocol = 'https';
                this.wsProtocol = 'wss';
            }
        }

        if (location.hostname.indexOf('portal.') === 0) {
            this.requestProxyServerUrl = 'https://proxy.nexuri.cz/fetch/';
        }

        // Mac mini
        if (location && location.hostname.indexOf('test.nexuri.dev') > -1) {
            this.requestProxyServerUrl = 'http://test.nexuri.dev:4000/fetch/';
        }

        // Create WebSocket Connection
        this.websocketService = new WebsocketService(this);

        // Open Websocket to Tyrion
        if (TyrionApiBackend.tokenExist()) {
            this.websocketService.openTyrionConnection(`${this.wsProtocol}://${TyrionApiBackend.host}/websocket/portal/`);
        }

        this.onlineStatus.subscribe((status: OnlineChangeStatus) => {
            if (!this.networkStatusObservables.hasOwnProperty(status.model_id)) {
                this.networkStatusObservables[status.model_id] = new BehaviorSubject<NetworkStatus>(status.online_state)
            } else {
                this.networkStatusObservables[status.model_id].next(status.online_state);
            }
        })
    }

    /********************************************************************************************************************
     * GENERIC REQUESTS
     *
     * - Also support Homer Api
     */
    protected abstract requestRestGeneral(request: RestRequest): Promise<RestResponse>;

    public requestRestPath<T>(method: string, path: string, body: Object, success: number[], contentType: string = 'application/json'): Promise<T> {
        // console.info('requestRestPath:: method', method, 'path: ', path);

        // If path contains http on beginning! or first char is "/" its on 100% api path from TyrionAPI
        // For example /login or /get_projects:{all}
        // But if contains https - for example https://homer.server.cz/get_something - it didnt used ${this.protocol}://${this.host}${path}
        if (path.charAt(0) === '/') {
            // console.info('Its a Tyrion API');
            return this.requestRest(method, `${TyrionApiBackend.protocol}://${TyrionApiBackend.host}${path}`, body, success, contentType);
        } else {
            return this.requestRest(method, path, body, success, contentType);
        }
    }

    public requestRest<T>(method: string, url: string, body: Object, success: number[], contentType: string): Promise<T> {
        // Create Request
        let request: RestRequest = new RestRequest(method, url, {}, body);

        // Set Token if Exist
        if (TyrionApiBackend.tokenExist()) {
            request.headers['X-Auth-Token'] = TyrionApiBackend.getToken();
        }
        // Set Becki Headers to Request
        request.headers['Becki-Version'] = BECKI_VERSION;
        request.headers['Content-Type'] = contentType;

        // Set Task - New Try
        this.increaseTasks();
        return this.requestRestGeneral(request)
            .then((response: RestResponse) => {
                this.decreaseTasks();
                if (success.indexOf(response.status) > -1) {
                    let res = response.body;
                    Object.defineProperty(res, '_code_', {
                        value: response.status,
                        enumerable: false,
                        writable: false
                    });
                    return <T>res;
                }

                // There is a log, when Errors are under 200 but json contains
                switch (response.status) {
                    case 400: {

                        if (response.body['state'] === 'error') {
                            throw BadRequest.fromRestResponse(response);
                        } else if (response.body['state'] === 'invalid_body') {
                            throw InvalidBody.fromRestResponse(response);
                        } else if (response.body['state'] === 'unsupported_exception') {
                            throw UnsupportedException.fromRestResponse(response);
                        }

                        // If there is not a state - Make a Critical error for sure
                        throw InternalServerError.fromRestResponse(response);
                    }
                    case 401:
                        throw UnauthorizedError.fromRestResponse(response);
                    case 403:
                        throw PermissionMissingError.fromRestResponse(response);
                    case 404:
                        throw ObjectNotFound.fromRestResponse(response);
                    case 422:
                        throw CodeCompileError.fromRestResponse(response);
                    case 477:
                        throw CodeError.fromRestResponse(response);
                    case 478:
                        throw CodeError.fromRestResponse(response);
                    case 500:
                        throw InternalServerError.fromRestResponse(response);
                    case 705:
                        throw UserNotValidatedError.fromRestResponse(response);
                    case 0:
                        throw new LostConnectionError();
                    default:
                        throw BugFoundError.fromRestResponse(response);
                }
            })
            .catch((response: RestResponse | IError) => {
                this.decreaseTasks();
                throw response;
            });
    }

    // TOKEN MANIPULATIONS
    /* tslint:disable */
    public static getToken(): string {
        return window.localStorage.getItem('auth_token');
    }

    public static tokenExist(): boolean {
        return !!window.localStorage.getItem('auth_token');
    }
    /* tslint:enable */

    public getBeckiBeta(): boolean {
        return this.beckiBeta;
    }

    private setToken(token: string, withRefreshPersonalInfo = true): void {
        window.localStorage.setItem('auth_token', token);
        if (withRefreshPersonalInfo) {
            this.refreshPersonInfo();
            this.websocketService.openTyrionConnection(`${this.wsProtocol}://${TyrionApiBackend.host}/websocket/portal/`);
        }
    }

    /**
     * Remove Token from Becki Memmory (Browser Memmory)
     */
    public unsetToken(): void {
        window.localStorage.removeItem('auth_token');
        this.refreshPersonInfo();
    }


    /********************************************************************************************************************
     * LOGIN / LOGOUT
     *
     * Github
     * Facebook
     * Get Person Info by token
     */
    public login(email: string, password: string): Promise<any> {
        if (!email || !password) {
            throw new Error('email and password required');
        }

        return this.__login({
            email: email,
            password: password
        }).then((body) => {
            this.setToken(body.auth_token);
            return body;
        }).then(body => {
            return JSON.stringify(body);
        });
    }

    public loginFacebook(redirectUrl: string): Promise<string> {
        return this.fBGetLoginURL({
            redirect_link: redirectUrl
        })
            .then(body => {
                this.setToken(body.auth_token);
                return body.link;
            });
    }

    /*
    public loginGitHub(redirectUrl: string): Promise<string> {
        return this.gitHubLogin(<ISocialNetworkLogin>{
            redirect_url: redirectUrl
        })
            .then(body => {
                this.setToken(body.auth_token, false);
                return body.redirect_url;
            });
    }
    */

    public logout(): Promise<any> {

        // TODO Líza tady odstranit spojení na websocket
        this.websocketService.closeTyrionConnection();

        return new Promise<any>((resolve, reject) => {
            this.__logout()
                .then((body) => {
                    this.unsetToken();
                    resolve(body);
                })
                .catch((err) => {
                    this.unsetToken();
                    reject(err);
                });
        });
    }

    // PERSON INFO
    public refreshPersonInfo(): void {
        // console.info('refreshPersonInfo');
        this.personInfoSnapshotDirty = true;
        if (TyrionApiBackend.tokenExist()) {
            this.personGetByToken()
                .then((lr: ILoginResult) => {
                    this.personInfoSnapshotDirty = false;
                    this.personInfoSnapshot = lr.person;
                    this.personInfo.next(this.personInfoSnapshot);


                    // https://app.inlinemanual.com/ - its used for Guidelines - also very important is User tracking data sets in backend - like user id..etc  - You can find this comment on another places
                    window['inlineManualTracking'] = {
                        uid: lr.person.id, // Only this field is mandatory
                        email: lr.person.email,
                        username: lr.person.nick_name,
                        name: lr.person.first_name + ' ' + lr.person.last_name,
                        first_name: lr.person.first_name,
                        last_name: lr.person.last_name,
                        created: lr.person.created,
                        updated: lr.person.updated,
                        roles: lr.roles.map(x => x.name),
                        plan: 'Not Defined'
                    };

                    // line_manual_player.setOptions({hide_trigger: true});

                    // Language for Inline Manual
                    window['InlineManualOptions'] = {
                        language: 'en'
                    };

                    // "a5e626bda2d1636e3c6b147fa55fbba0d6f193a96029bb830966c223e6f40bca"
                    // "a5e626bda2d1636e3c6b147fa55fbba0d6f193a96029bb830966c223e6f40bca"
                    // "a5e626bda2d1636e3c6b147fa55fbba0d6f193a96029bb830966c223e6f40bca"

                    window['intercomSettings'] = {
                        app_id: 'cnrrdzsk',
                        id: lr.person.id,
                        user_id: lr.person.id,
                        name: lr.person.first_name + ' ' + lr.person.last_name, // Full name
                        email: lr.person.email, // Email address
                        user_hash: lr.hmac,
                        created_at: lr.person.created, // Signup Date
                        alignment: 'left',
                        horizontal_padding: 20,
                        vertical_padding: 40
                    };

                })
                .catch((error) => {
                    console.error(error);
                    this.unsetToken(); // TODO: maybe check error type before force logout user
                    this.personInfoSnapshotDirty = false;
                    this.personInfoSnapshot = null;
                    this.personInfo.next(this.personInfoSnapshot);
                });
        } else {
            this.personInfoSnapshotDirty = false;
            this.personInfoSnapshot = null;
            this.personInfo.next(this.personInfoSnapshot);
        }
    }


    /********************************************************************************************************************
     * Websocket
     *
     * Github
     * Facebook
     * Get Person Info by token
     */

    public getWebsocketService(): WebsocketService {
        return this.websocketService;
    }

    /* tslint:disable */
    public static uuid(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            // tslint:disable-next-line:no-bitwise
            let r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }
    /* tslint:enable */

    public stopWebSocketCommunication(): void {
        this.websocketService.closeTyrionConnection();
    }

    public observeNetworkStatus(id: string): Observable<NetworkStatus> {
        if (!this.networkStatusObservables.hasOwnProperty(id)) {
            this.networkStatusObservables[id] = new BehaviorSubject<NetworkStatus>('SYNCHRONIZATION_IN_PROGRESS')
        }
        return this.networkStatusObservables[id];
    }
}
